import { Money, dec, localDate } from "@despejo/domain";
import { type EngineParameter, type UsedParameter, decimalField, pickParameter, usedParameter } from "./params.js";

/**
 * Annual corporate checklist for a persona jurídica (plan §4.2, Phase 5):
 * Impuesto a las Personas Jurídicas (169), TEC (175 / 184), RTBF, Renta
 * (102, or the RTS quarterly 105) and pagos parciales. Pure: the caller passes
 * the generated obligations (for dates and status) and the parameter rows
 * (for the expected amounts). docs/tax/PERSONAS_JURIDICAS.md, TEC.md, RTBF.md.
 */

export const CORPORATE_CHECKLIST_VERSION = "corporate-checklist@1.1.0";

export interface ChecklistObligation {
  id: string;
  obligation_code: string;
  period_key: string;
  due_date: string;
  filing_status: string;
  payment_status: string;
  has_payment: boolean;
  provisional: boolean;
  amount_due: string | null;
  amount_paid: string;
}

export interface CorporateChecklistInput {
  /** Calendar year Y the checklist covers (everything due during Y). */
  year: number;
  regime: "GENERAL" | "RTS";
  /** Registered as inactive (Persona Jurídica Inactiva): 15% PJ rate, Form 272, TEC via 184. */
  inactive: boolean;
  /** Gross income of the last Renta return filed before 1 Jan Y (Ley 9428 art. 3). */
  priorGrossIncome: string | null;
  /** Capital neto / social declared for fiscal year Y-1 (Ley 5923 art. 7-8). */
  capitalForTec: string | null;
  obligations: ChecklistObligation[];
  /**
   * Pagos parciales for Y (key -> amount): the amount that counts per
   * installment (official Cuenta Integral figure, an approved request, or the
   * computed one) and, optionally, where it comes from.
   */
  partialPayments?: { key: string; amount: string | null; basis?: string | null }[];
}

export type ChecklistStatus = "DONE" | "PENDING" | "OVERDUE" | "NOT_GENERATED";

export interface ChecklistItem {
  code: string;
  label: string;
  form: string | null;
  periodKey: string;
  dueDate: string | null;
  obligationId: string | null;
  expectedAmount: string | null;
  /** How the expected amount was derived, for the drill-down. */
  amountBasis: string | null;
  provisional: boolean;
  status: ChecklistStatus;
  notes: string[];
}

export interface CorporateChecklist {
  engine: string;
  year: number;
  items: ChecklistItem[];
  parameters: UsedParameter[];
  provisional: boolean;
}

const DONE_FILING = new Set(["FILED", "FILED_LATE", "AMENDED", "NOT_APPLICABLE", "WAIVED"]);
const DONE_PAYMENT = new Set(["PAID", "PAID_LATE", "NOT_DUE"]);

function statusOf(o: ChecklistObligation | undefined, today: string): ChecklistStatus {
  if (!o) return "NOT_GENERATED";
  const filed = DONE_FILING.has(o.filing_status);
  const paid = !o.has_payment || DONE_PAYMENT.has(o.payment_status);
  if (filed && paid) return "DONE";
  return o.due_date < today ? "OVERDUE" : "PENDING";
}

export function corporateChecklist(input: CorporateChecklistInput, params: readonly EngineParameter[], today: string): CorporateChecklist {
  const y = input.year;
  const used: UsedParameter[] = [];
  const items: ChecklistItem[] = [];
  const find = (code: string, key: string) => input.obligations.find((o) => o.obligation_code === code && o.period_key === key);
  const use = (p: EngineParameter) => {
    if (!used.some((u) => u.key === p.key && u.effective_from === p.effective_from)) used.push(usedParameter(p));
    return p.review_status !== "APPROVED";
  };
  const push = (code: string, label: string, form: string | null, key: string, extra: Partial<ChecklistItem> = {}) => {
    const o = find(code, key);
    items.push({
      code,
      label,
      form,
      periodKey: key,
      dueDate: o?.due_date ?? null,
      obligationId: o?.id ?? null,
      expectedAmount: null,
      amountBasis: null,
      provisional: o?.provisional ?? false,
      status: statusOf(o, today),
      notes: [],
      ...extra,
    });
  };

  // ------------------------------------------- Personas Jurídicas (169)
  {
    const notes: string[] = [];
    let amount: string | null = null;
    let basis: string | null = null;
    let provisional = false;
    const rates = pickParameter(params, "PERSONAS_JURIDICAS_RATES", localDate(y, 1, 1));
    const sbRow = pickParameter(params, "SALARIO_BASE", localDate(y, 1, 1));
    if (rates && sbRow) {
      const draftRates = use(rates);
      const draftSb = use(sbRow);
      provisional = draftRates || draftSb;
      const sb = Money.of(decimalField(sbRow.value, "amount"));
      const brackets = rates.value.brackets as Record<string, string | null>[];
      let rate: string | null = null;
      let why = "";
      if (input.inactive) {
        rate = brackets.find((b) => b.case === "NOT_IN_RUT_OR_NON_CONTRIBUTOR_OR_INACTIVE")?.rate ?? null;
        why = "inactiva";
      } else if (input.regime === "RTS") {
        rate = brackets.find((b) => b.case === "RTS")?.rate ?? null;
        why = "régimen simplificado";
      } else if (input.priorGrossIncome !== null) {
        const gi = Money.of(input.priorGrossIncome);
        const hit = brackets.find(
          (b) =>
            b.case === "INCOME_TAXPAYER" &&
            (b.gross_income_min_sb == null || gi.cmp(sb.times(b.gross_income_min_sb)) >= 0) &&
            (b.gross_income_max_sb_exclusive == null || gi.cmp(sb.times(b.gross_income_max_sb_exclusive)) < 0),
        );
        rate = hit?.rate ?? null;
        why = `ingresos brutos ${gi.toFixed(2)} (${gi.dividedBy(sb.amount.toString()).round(1).toFixed(1)} salarios base)`;
      } else {
        notes.push("Sin ingresos brutos de la última declaración de renta: no se puede fijar la tarifa (si está omisa, Hacienda la clasifica como morosa).");
      }
      if (rate) {
        amount = sb.times(rate).round(2).toFixed(2);
        basis = `${dec(rate).times(100).toString()}% × salario base ${sb.toFixed(2)} (${why})`;
      }
    } else {
      notes.push("Faltan los parámetros de tarifa o salario base.");
    }
    push("PERSONAS_JURIDICAS", "Impuesto a las personas jurídicas", "169", String(y), { expectedAmount: amount, amountBasis: basis, notes, ...(provisional ? { provisional: true } : {}) });
  }

  // ------------------------------------------------------ TEC (175/184)
  {
    const notes: string[] = [];
    let amount: string | null = null;
    let basis: string | null = null;
    let provisional = false;
    const tec = pickParameter(params, "TEC_ANNUAL_BRACKETS", localDate(y - 1, 12, 31));
    if (tec && input.capitalForTec !== null) {
      provisional = use(tec);
      const cap = Money.of(input.capitalForTec);
      const hit = (tec.value.brackets as Record<string, string>[]).find(
        (b) => (b.min_exclusive === undefined || cap.cmp(Money.of(b.min_exclusive)) > 0) && (b.max_inclusive === undefined || cap.cmp(Money.of(b.max_inclusive)) <= 0),
      );
      if (hit) {
        amount = Money.of(hit.amount!).toFixed(2);
        basis = `Capital ${cap.toFixed(2)} → tramo de ${amount}`;
      }
    } else if (!tec) {
      notes.push("Falta el parámetro TEC_ANNUAL_BRACKETS.");
    } else {
      notes.push("Indique el capital declarado para calcular el monto.");
    }
    notes.push("La base (capital neto según la ley vs capital social según Hacienda) está por verificar.");
    const form = input.inactive || input.regime === "RTS" ? "184" : "175";
    push("TEC", "Timbre de Educación y Cultura", form, String(y - 1), { expectedAmount: amount, amountBasis: basis, notes, ...(provisional ? { provisional: true } : {}) });
  }

  // ------------------------------------------------------------- RTBF
  push("RTBF", "Declaración de beneficiarios finales (RTBF)", null, String(y), { notes: ["Se presenta en Central Directo (BCCR) con firma digital; guarde el acuse."] });

  // ------------------------------------------------------------ Renta
  if (input.inactive) {
    push("FORM_272", "Declaración de personas jurídicas inactivas", "272", String(y - 1));
  } else if (input.regime === "RTS") {
    for (let q = 1; q <= 4; q++) push("RTS_UTILIDADES_PJ", `Utilidades RTS T${q}`, "105", `${y}-Q${q}`);
  } else {
    push("RENTA_PJ", `Impuesto sobre las utilidades ${y - 1}`, "102", String(y - 1));
    const pp = new Map((input.partialPayments ?? []).map((p) => [p.key, p]));
    for (const k of ["P1", "P2", "P3"]) {
      const amt = pp.get(k)?.amount ?? null;
      const basis = pp.get(k)?.basis ?? "25% de la base (art. 22 Ley 7092)";
      push("PAGOS_PARCIALES", `Pago parcial ${k.slice(1)} de ${y}`, null, `${y}:${k}`, { expectedAmount: amt, amountBasis: amt ? basis : null });
    }
  }

  return { engine: CORPORATE_CHECKLIST_VERSION, year: y, items, parameters: used, provisional: items.some((i) => i.provisional) };
}
